import { TabPanel } from 'devextreme-react';
import moment from 'moment';
import { equals } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import '../../assets/tasks.scss';
import { useBreadcrumbs } from '../../contexts/breadcrumbsContext';
import { useTranslation } from '../../contexts/translation';
import { CustomersProjectsForm } from '../../forms/customersProjects/CustomersProjectsForm';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import { PageContentWrapper } from '../../layouts/contentWrapper/PageContentWrapper';
import { CustomerProject } from '../../types/customerProject.types';
import { DetailPageParams } from '../../types/pageParams.types';
import { WorkSheet } from '../../types/workSheet.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { MomentDateTimeSecondsSerializationFormat } from '../../utils/dates.types';
import { STORAGE_KEYS } from '../../utils/localStorageKeys';
import { swrConfig } from '../Assets/AssetsEdit';
import { timeSpanDecimal } from '../Tasks/Columns';
import { CustomerProjectsHistoryTab } from './Tabs/CustomerProjectsHistoryTab';
import {
  CustomerProjectsTasksTab,
  CustomerProjectsTasksTabFilterData,
} from './Tabs/CustomerProjectsTasksTab';
import {
  CustomerProjectsTasksWorkSheetsFilterData,
  CustomerProjectsWorkSheetsTab,
} from './Tabs/CustomerProjectsWorkSheetsTab';

export const tabsIndexes = {
  TASKS: 0,
  WORKSHEETS: 1,
  HISTORY: 2,
};

export function CustomersProjectsEdit() {
  const { translate } = useTranslation();
  const transFormName = 'CustomersProjects.Update';

  const { id } = useParams<DetailPageParams>();
  const { setValue } = useBreadcrumbs();
  const [currentTab, setCurrentTab] = useSessionStorage(
    STORAGE_KEYS.CUSTOMER_PROJECT_TAB,
    tabsIndexes.TASKS,
  );

  const [filterDataTasks, setFilterDataTasks] = useState<CustomerProjectsTasksTabFilterData>({
    dateFrom: moment()
      .add(-30, 'days')
      .startOf('day')
      .format(MomentDateTimeSecondsSerializationFormat),
    dateTo: moment().endOf('day').format(MomentDateTimeSecondsSerializationFormat),
  });
  const [filterDataWorksheets, setFilterDataWorksheets] =
    useState<CustomerProjectsTasksWorkSheetsFilterData>({
      dateFrom: moment()
        .add(-30, 'days')
        .startOf('day')
        .format(MomentDateTimeSecondsSerializationFormat),
      dateTo: moment().endOf('day').format(MomentDateTimeSecondsSerializationFormat),
    });

  useEffect(() => {
    setValue({
      pageName: 'Edit customer project',
      steps: [
        { name: 'Home', pathToCutCount: 3 },
        { name: 'Customers projects', pathToCutCount: 2 },
      ],
    });
    return () => {
      setValue(null);
    };
  }, []);

  const {
    data: formData,
    error,
    mutate: mutateCustomerProject,
  } = useSWR<CustomerProject>(`${API_URL.CUSTOMER_PROJECT_ITEM}?id=${id}`, fetcher);

  const { data: totalElapsedTime } = useSWR<any>(
    `${API_URL.TASK_TOTAL_ELAPSED_TIME_FILTERED_BY_PROJECT}?projectId=${id}`,
    fetcher,
  );

  const { data: tasksCount } = useSWR<number>(
    `${API_URL.TASK_BY_CUSTOMER_PROJECT_COUNT}?projectId=${id}`,
    fetcher,
    swrConfig,
  );

  const { data: workSheets } = useSWR<WorkSheet[]>(
    `${API_URL.WORKSHEET_LIST_FOR_CUSTOMER_PROJECT}?customerProjectId=${id}`,
    fetcher,
    swrConfig,
  );

  const { data: historyItems } = useSWR(
    `${API_URL.CUSTOMER_PROJECT_HISTORY_LIST}?parentId=${id}`,
    fetcher,
    swrConfig,
  );

  const tabs = [
    {
      title: `${translate!('Tasks', transFormName)} ${
        tasksCount || tasksCount === 0 ? `(${tasksCount})` : ''
      }`,
      index: tabsIndexes.TASKS,
      component: equals(currentTab, 0) && (
        <CustomerProjectsTasksTab
          id={id}
          active={equals(0, currentTab)}
          customerId={formData?.customerId}
          customerProjectId={Number(id)}
          topLevelAssetId={formData?.assetTopLevelParenId}
          assetId={formData?.assetId}
          filterData={filterDataTasks}
          setFilterData={setFilterDataTasks}
        />
      ),
    },
    {
      title: `${translate!('Worksheets', transFormName)} ${
        workSheets ? `(${workSheets.length})` : ''
      }`,
      index: tabsIndexes.WORKSHEETS,
      component: equals(currentTab, 1) && (
        <CustomerProjectsWorkSheetsTab
          id={id}
          active={equals(1, currentTab)}
          customerProjectId={Number(id)}
          filterData={filterDataWorksheets}
          setFilterData={setFilterDataWorksheets}
        />
      ),
    },
    {
      title: `${translate!('History', transFormName)} ${
        historyItems ? `(${historyItems.length})` : ''
      }`,
      index: tabsIndexes.HISTORY,
      component: equals(currentTab, 2) && (
        <CustomerProjectsHistoryTab
          id={id}
          active={equals(2, currentTab)}
          historyItems={historyItems || []}
        />
      ),
    },
  ];

  const formEl = useMemo(() => {
    if (!formData) return <></>;

    return (
      <CustomersProjectsForm
        onSubmit={() => undefined}
        formData={{
          ...formData,
          plannedTimeBudget: formData.plannedTimeBudget
            ? timeSpanDecimal(formData.plannedTimeBudget)
            : null,
        }}
        isEdit
        responsibleInternalUserId={formData.responsibleInternalUserId}
        totalElapsedTime={totalElapsedTime}
        projectId={Number(id)}
        onSuccess={() => {
          mutateCustomerProject();
        }}
      />
    );
  }, [formData, totalElapsedTime]);

  return (
    <PageContentWrapper
      error={error}
      loading={!formData}
      heading={translate!('Edit customer project', transFormName)}
    >
      <>
        {formData && totalElapsedTime && <>{formEl}</>}
        <div style={{ marginTop: 15 }}>
          <TabPanel
            selectedIndex={currentTab}
            dataSource={tabs}
            itemRender={(a) => a.component}
            activeStateEnabled={true}
            onTitleClick={(e: any) => setCurrentTab(e.itemIndex)}
            deferRendering={false}
            focusStateEnabled={false}
          />
        </div>
      </>
    </PageContentWrapper>
  );
}

export default CustomersProjectsEdit;
